import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Sparkles, Lock, FileText } from "lucide-react";
import { getAuditType } from "@/lib/auditTypes";
import { toast } from "sonner";
import { downloadProtectedFile } from "@/lib/assets";

function markdownToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  let inTable = false;
  let tableRows = [];

  const flushList = () => { if (inList) { html += "</ul>"; inList = false; } };
  const flushTable = () => {
    if (inTable && tableRows.length) {
      html += "<table>";
      tableRows.forEach((r, i) => {
        const cells = r.split("|").map((c) => c.trim()).filter((c) => c.length);
        if (cells.every((c) => /^-+$/.test(c))) return;
        const tag = i === 0 ? "th" : "td";
        html += "<tr>" + cells.map((c) => `<${tag}>${c}</${tag}>`).join("") + "</tr>";
      });
      html += "</table>";
      tableRows = []; inTable = false;
    }
  };

  const inlineFormat = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
     .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
     .replace(/`([^`]+)`/g, "<code>$1</code>");

  for (const raw of lines) {
    const l = raw.trimEnd();
    if (l.startsWith("|") && l.endsWith("|")) { inTable = true; tableRows.push(l); continue; }
    else { flushTable(); }
    if (l.startsWith("## ")) { flushList(); html += `<h2>${inlineFormat(l.slice(3))}</h2>`; }
    else if (l.startsWith("### ")) { flushList(); html += `<h3>${inlineFormat(l.slice(4))}</h3>`; }
    else if (l.startsWith("# ")) { flushList(); html += `<h2>${inlineFormat(l.slice(2))}</h2>`; }
    else if (l.startsWith("- ") || l.startsWith("* ")) {
      if (!inList) { html += "<ul>"; inList = true; }
      html += `<li>${inlineFormat(l.slice(2))}</li>`;
    } else if (!l.trim()) { flushList(); }
    else { flushList(); html += `<p>${inlineFormat(l)}</p>`; }
  }
  flushTable();
  flushList();
  return html;
}

export default function ReportPage() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(() => api.get(`/audits/${id}`).then((r) => setAudit(r.data)).catch(() => toast.error("Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (audit?.frozen) { toast.error("Audit congelato — crea una nuova revisione per rigenerare"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post(`/audits/${id}/generate-report`, { extra_instructions: null });
      for (let attempt = 0; attempt < 150; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const { data: job } = await api.get(`/generation-jobs/${data.job_id}`);
        if (job.status === "done") { toast.success("Relazione rigenerata"); load(); return; }
        if (job.status === "error") throw new Error(job.error || "Errore generazione");
      }
      throw new Error("Generazione troppo lunga. Riprova più tardi.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    try {
      await downloadProtectedFile(`/audits/${id}/pdf`, `audit-${audit?.type || "report"}-${id.slice(0, 8)}.pdf`);
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore download PDF")); }
  };

  const downloadDocx = async () => {
    try {
      await downloadProtectedFile(
        `/audits/${id}/docx?source=report`,
        `verbale-${(audit?.company_name || "audit").replace(/[^\w\-]+/g, "_").slice(0, 40)}-v${audit?.version || 1}.docx`
      );
    } catch (err) { toast.error(getApiErrorMessage(err, "Errore download Word")); }
  };

  if (!audit) return <div className="p-10 text-slate-500">Caricamento…</div>;

  return (
    <div className="pb-16">
      <div className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-slate-200">
         <div className="p-5 md:px-10 max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/audit/${id}`} className="text-slate-500 hover:text-slate-900 transition-colors duration-200"><ArrowLeft className="h-5 w-5" /></Link>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Relazione conclusiva</div>
              <h1 className="font-display font-black text-xl md:text-2xl truncate">{audit.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {audit.frozen && (
              <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-900 text-white inline-flex items-center gap-1">
                <Lock className="h-2.5 w-2.5" /> v{audit.version || 1} · Storico
              </span>
            )}
            {!audit.frozen && (
              <Button variant="outline" onClick={regenerate} disabled={generating} data-testid="regenerate-btn" className="h-10">
                <Sparkles className="h-4 w-4" /> {generating ? "…" : "Rigenera"}
              </Button>
            )}
            <Button onClick={downloadDocx} variant="outline" data-testid="download-docx-btn" className="h-10">
              <FileText className="h-4 w-4" /> Word
            </Button>
            <Button onClick={downloadPdf} data-testid="download-pdf-btn" className="h-10 bg-slate-900 hover:bg-slate-800">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
      </div>

       <div className="p-5 md:p-10 max-w-5xl mx-auto">
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Azienda</div>
            <div className="font-semibold text-sm mt-1 truncate">{audit.company_name}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Framework</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(audit.types || [audit.type]).filter(Boolean).map((t) => {
                const meta = getAuditType(t);
                return <span key={t} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${meta.badge_bg} ${meta.badge_text}`}>{meta.label}</span>;
              })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Score</div>
            <div className="font-display font-black text-xl mt-1">{audit.score}%</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Data</div>
            <div className="font-mono-plex text-sm mt-1">{audit.updated_at?.slice(0,10)}</div>
          </div>
        </div>

        {audit.report ? (
          <article
            data-testid="report-content"
            className="report-content bg-white border border-slate-200 rounded-lg p-8 md:p-12 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(markdownToHtml(audit.report), { USE_PROFILES: { html: true } }) }}
          />
        ) : (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center">
            <Sparkles className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <div className="font-display font-bold text-lg mb-2">Nessuna relazione ancora generata</div>
            <p className="text-slate-500 text-sm mb-6">Genera la relazione dall'audit per visualizzarla qui.</p>
            <Button onClick={regenerate} disabled={generating} data-testid="empty-generate-btn" className="bg-slate-900 hover:bg-slate-800">
              <Sparkles className="h-4 w-4" /> Genera relazione
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
